import React, { useState, useRef, useMemo } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  ZoomIn,
  Download,
  Calendar,
  MapPin,
  Grid,
  List,
  Play,
} from "lucide-react";
import SecondaryNavbar from "../../components/common/SecondaryNavbar";
import SecondaryHero from "../../components/common/SecondaryHero";

const Gallery = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Sample gallery items
  const galleryItems = [
    {
      id: 1,
      title: "Forest Diversion Mapping",
      category: "drone",
      image:
        "https://everainglobal.com/wp-content/uploads/2024/09/Firefly-DGPS-instrument-for-field-survey-76244-1170x694.jpg",
      date: "2024-01-15",
      location: "Western Ghats, India",
      description:
        "Aerial mapping of forest areas for conservation and development planning.",
      tags: ["Drone", "Forest", "Mapping", "Environmental"],
      type: "image",
    },
    {
      id: 2,
      title: "Urban Planning GIS",
      category: "gis",
      image:
        "https://www.csm.tech/admin/storage/app/uploads/TwitterImage1747830437.jpeg",
      date: "2024-02-20",
      location: "Metro City",
      description:
        "Comprehensive GIS analysis for urban development and infrastructure planning.",
      tags: ["GIS", "Urban", "Planning", "Analysis"],
      type: "image",
    },
    {
      id: 3,
      title: "Mining Site Survey",
      category: "mining",
      image:
        "https://jinn.careers/wp-content/uploads/2022/08/valeria-fursa-PDjpA1yeonw-unsplash.jpg",
      date: "2024-01-08",
      location: "Mineral Rich Zone",
      description:
        "Detailed topographic survey of mining areas for resource management.",
      tags: ["Mining", "Survey", "Topography", "Resources"],
      type: "image",
    },
    {
      id: 4,
      title: "Environmental Impact Assessment",
      category: "environment",
      image:
        "https://agribusinessedu.com/wp-content/uploads/2022/01/the-environmental-impact-of-agriculture-during-the-covid-19-pandemic-and-sdg-2022-01-17-032757.jpg",
      date: "2024-03-10",
      location: "Coastal Region",
      description:
        "Environmental impact analysis for coastal development projects.",
      tags: ["Environmental", "Assessment", "Coastal", "EIA"],
      type: "image",
    },
    {
      id: 5,
      title: "Infrastructure Development",
      category: "infrastructure",
      image:
        "https://cicconstruction.com/app/uploads/2023/06/building-for-progress-why-infrastructure-development-is-worth-the-trouble-jpg.webp",
      date: "2024-02-28",
      location: "Industrial Zone",
      description:
        "GIS-based planning for large-scale infrastructure development.",
      tags: ["Infrastructure", "Development", "Planning", "GIS"],
      type: "image",
    },
    {
      id: 6,
      title: "Wildlife Habitat Mapping",
      category: "drone",
      image:
        "https://www.green.earth/hubfs/7%20strategies%20for%20Protecting%20Wildlife-Pillar%20%20Protecting%20Vital%20Habitats_featured.png",
      date: "2024-01-30",
      location: "National Park",
      description:
        "Drone-based habitat mapping for wildlife conservation efforts.",
      tags: ["Wildlife", "Habitat", "Conservation", "Drone"],
      type: "video",
    },
    {
      id: 7,
      title: "Agricultural Land Survey",
      category: "gis",
      image:
        "https://5.imimg.com/data5/SELLER/Default/2022/3/RS/XF/ZF/92188465/5-land-surveyors-5-nqm5c-jpg.jpg",
      date: "2024-03-05",
      location: "Farmland Region",
      description:
        "Precision agriculture mapping using advanced GIS technologies.",
      tags: ["Agriculture", "Survey", "Precision", "GIS"],
      type: "image",
    },
    {
      id: 8,
      title: "Water Resource Management",
      category: "environment",
      image:
        "https://rajras.in/wp-content/uploads/2018/02/Water-Resources-of-Rajasthan.jpg",
      date: "2024-02-14",
      location: "River Basin",
      description: "Water resource mapping and management planning.",
      tags: ["Water", "Resources", "Management", "Mapping"],
      type: "image",
    },
    {
      id: 9,
      title: "Real-time GIS Dashboard Development",
      category: "software",
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      date: "2024-03-18",
      location: "Remote",
      description:
        "Interactive dashboard for real-time GIS data visualization and analysis using React and Mapbox.",
      tags: ["React", "Mapbox", "Dashboard", "Real-time", "Data Visualization"],
      type: "image",
    },
    {
      id: 10,
      title: "Drone Data Processing Pipeline",
      category: "software",
      image:
        "https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      date: "2024-03-12",
      location: "Cloud Infrastructure",
      description:
        "Automated pipeline for processing drone imagery using Python, OpenCV, and cloud computing.",
      tags: ["Python", "OpenCV", "Cloud", "Automation", "Image Processing"],
      type: "image",
    },
    {
      id: 11,
      title: "Mobile GIS Application",
      category: "software",
      image:
        "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      date: "2024-03-08",
      location: "Field Operations",
      description:
        "Cross-platform mobile app for field data collection with offline GIS capabilities and sync features.",
      tags: ["React Native", "Mobile", "Offline", "Data Collection", "GIS"],
      type: "image",
    },
    {
      id: 12,
      title: "Spatial Database Optimization",
      category: "software",
      image:
        "https://images.unsplash.com/photo-1627398242454-45a1465c2479?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      date: "2024-03-03",
      location: "Data Center",
      description:
        "Performance optimization of PostGIS database handling large-scale spatial data for faster queries.",
      tags: ["PostGIS", "Database", "Optimization", "Performance", "SQL"],
      type: "image",
    },
  ];

  // Calculate dynamic counts using useMemo for optimization
  const { categories } = useMemo(() => {
    // Calculate category counts
    const categoryCounts = galleryItems.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {});

    const categories = [
      { id: "all", name: "All Projects", count: galleryItems.length },
      { id: "drone", name: "Drone Surveys", count: categoryCounts.drone || 0 },
      { id: "gis", name: "GIS Mapping", count: categoryCounts.gis || 0 },
      {
        id: "environment",
        name: "Environmental",
        count: categoryCounts.environment || 0,
      },
      {
        id: "mining",
        name: "Mining Surveys",
        count: categoryCounts.mining || 0,
      },
      {
        id: "infrastructure",
        name: "Infrastructure",
        count: categoryCounts.infrastructure || 0,
      },
      {
        id: "software",
        name: "Software Development",
        count: categoryCounts.software || 0,
      },
    ];

    return { categories };
  }, [galleryItems]);

  // Filter gallery items based on category and search
  const filteredItems = galleryItems.filter((item) => {
    const matchesCategory =
      selectedCategory === "all" || item.category === selectedCategory;
    const matchesSearch =
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  // Count types for stats display
  const stats = useMemo(() => {
    const totalItems = galleryItems.length;
    const imageCount = galleryItems.filter(
      (item) => item.type === "image"
    ).length;
    const videoCount = galleryItems.filter(
      (item) => item.type === "video"
    ).length;
    const uniqueLocations = [
      ...new Set(galleryItems.map((item) => item.location)),
    ].length;
    const uniqueTags = [...new Set(galleryItems.flatMap((item) => item.tags))]
      .length;

    return {
      totalItems,
      imageCount,
      videoCount,
      uniqueLocations,
      uniqueTags,
    };
  }, [galleryItems]);

  // Open lightbox
  const openLightbox = (item, index) => {
    setSelectedImage(item);
    setCurrentIndex(index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 0.6,
      },
    },
  };

  return (
    <div className="overflow-hidden">
      <SecondaryNavbar />

      <div className="min-h-screen tracking-wide bg-gradient-to-br from-gray-50 to-white">
        <SecondaryHero
          pageName="Projects Gallery"
          pageHead1="Our"
          pageHead2="Work"
          pageHead3="in"
          pageHead4="Action"
          pageDesc="Witness the precision and innovation behind every project through our comprehensive visual portfolio"
          bgImgUrl="https://thelucknowtribune.org/wp-content/uploads/2024/12/GettyImages-920156890-5b70e90bc9e77c0050218712.jpg"
        />

        {/* Gallery Content */}
        <section ref={sectionRef} className="py-6 lg:py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Stats Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <div className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-primaryTextColor mb-4 font-lexend">
                  Gallery Overview
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-xl">
                    <div className="text-2xl font-bold text-brandBlue">
                      {stats.totalItems}
                    </div>
                    <div className="text-sm text-secondaryTextColor">
                      Total Projects
                    </div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-xl">
                    <div className="text-2xl font-bold text-brandGreen">
                      {stats.imageCount}
                    </div>
                    <div className="text-sm text-secondaryTextColor">
                      Images
                    </div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-xl">
                    <div className="text-2xl font-bold text-red-500">
                      {stats.videoCount}
                    </div>
                    <div className="text-sm text-secondaryTextColor">
                      Videos
                    </div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-xl">
                    <div className="text-2xl font-bold text-purple-500">
                      {stats.uniqueLocations}
                    </div>
                    <div className="text-sm text-secondaryTextColor">
                      Locations
                    </div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-xl">
                    <div className="text-2xl font-bold text-orange-500">
                      {stats.uniqueTags}
                    </div>
                    <div className="text-sm text-secondaryTextColor">
                      Unique Tags
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Filters and Controls */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8">
                {/* Search Bar */}
                <div className="relative flex-1 max-w-md w-full">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brandBlue focus:border-transparent transition-all duration-300"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* View Mode Toggle and Results Count */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-secondaryTextColor hidden sm:block">
                    Showing{" "}
                    <span className="font-semibold text-brandBlue">
                      {filteredItems.length}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-brandBlue">
                      {galleryItems.length}
                    </span>{" "}
                    projects
                  </div>
                  <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-2xl p-1">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded-xl transition-all duration-300 ${
                        viewMode === "grid"
                          ? "bg-brandBlue text-white"
                          : "text-gray-600 hover:text-brandBlue"
                      }`}
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded-xl transition-all duration-300 ${
                        viewMode === "list"
                          ? "bg-brandBlue text-white"
                          : "text-gray-600 hover:text-brandBlue"
                      }`}
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                      selectedCategory === category.id
                        ? "bg-brandBlue text-white shadow-lg"
                        : "bg-white text-gray-700 border border-gray-300 hover:border-brandBlue hover:text-brandBlue"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {category.name} ({category.count})
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Gallery Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className={`${
                viewMode === "grid"
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  : "space-y-6"
              }`}
            >
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  variants={itemVariants}
                  className={`group relative bg-white rounded-3xl border border-gray-200 hover:border-brandBlue/30 transition-all duration-500 hover:shadow-2xl overflow-hidden ${
                    viewMode === "list" ? "flex" : ""
                  }`}
                >
                  {/* Image/Video Thumbnail */}
                  <div
                    className={`relative overflow-hidden ${
                      viewMode === "list"
                        ? "w-64 flex-shrink-0"
                        : "aspect-square"
                    }`}
                  >
                    <div className="relative w-full h-full">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>

                    {/* Overlay Actions */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center space-x-3 opacity-0 group-hover:opacity-100">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => openLightbox(item, index)}
                        className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
                      >
                        <ZoomIn className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center text-gray-700 hover:text-brandBlue transition-all duration-300"
                      >
                        <Download className="w-5 h-5" />
                      </motion.button>
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span
                        className="px-3 py-1 rounded-full text-white text-xs font-semibold backdrop-blur-sm"
                        style={{
                          background: `linear-gradient(135deg, ${
                            item.category === "drone"
                              ? "#0071BD"
                              : item.category === "gis"
                              ? "#6DA512"
                              : item.category === "environment"
                              ? "#10B981"
                              : item.category === "mining"
                              ? "#F59E0B"
                              : "#8B5CF6"
                          }, ${
                            item.category === "drone"
                              ? "#005a9e"
                              : item.category === "gis"
                              ? "#5a8a0f"
                              : item.category === "environment"
                              ? "#0d966c"
                              : item.category === "mining"
                              ? "#d97706"
                              : "#7c3aed"
                          })`,
                        }}
                      >
                        {
                          categories.find((cat) => cat.id === item.category)
                            ?.name
                        }
                      </span>
                    </div>

                    {/* Video Indicator */}
                    {item.type === "video" && (
                      <div className="absolute top-4 right-4">
                        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                          <Play className="w-4 h-4 text-white" fill="white" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className={`p-6 ${viewMode === "list" ? "flex-1" : ""}`}>
                    <h3 className="text-xl font-bold text-primaryTextColor mb-3 font-lexend group-hover:text-brandBlue transition-colors duration-300">
                      {item.title}
                    </h3>

                    <p className="text-secondaryTextColor leading-relaxed mb-4">
                      {item.description}
                    </p>

                    {/* Meta Information */}
                    <div className="flex items-center space-x-4 text-sm text-secondaryTextColor mb-4">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(item.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>{item.location}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-3 py-1 bg-gray-100 text-secondaryTextColor text-xs font-medium rounded-lg hover:bg-brandBlue hover:text-white transition-all duration-300 cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Empty State */}
            {filteredItems.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Search className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-primaryTextColor mb-4">
                  No projects found
                </h3>
                <p className="text-secondaryTextColor max-w-md mx-auto">
                  Try adjusting your search criteria or browse all categories to
                  find what you're looking for.
                </p>
              </motion.div>
            )}
          </div>
        </section>

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImage && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedImage(null)}
                className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                >
                  {/* Lightbox Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                      <h3 className="text-2xl font-bold text-primaryTextColor font-lexend">
                        {selectedImage.title}
                      </h3>
                      <p className="text-secondaryTextColor">
                        {selectedImage.description}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedImage(null)}
                      className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="aspect-video bg-gray-100 rounded-2xl overflow-hidden mb-6 relative">
                    {selectedImage.type === "video" ? (
                      <video
                        src={selectedImage.image}
                        controls
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img
                        src={selectedImage.image}
                        alt={selectedImage.title}
                        className="w-full h-full object-cover"
                      />
                    )}

                    {/* Optional gradient overlay for better readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Gallery;
